import React from 'react';
import { useFeatureToggle } from '../hooks/useFeatureToggle';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';

interface FeatureFlagRouteProps {
  featureKey: string;
  children: React.ReactNode;
}

/**
 * Wraps a route component with a feature flag check.
 * Shows a loading spinner while checking, and a 403 page if the flag is disabled.
 */
const FeatureFlagRoute: React.FC<FeatureFlagRouteProps> = ({ featureKey, children }) => {
  const { isEnabled, isLoading } = useFeatureToggle(featureKey);
  const navigate = useNavigate();
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-muted-foreground">
        <div className="rounded-full bg-muted p-4">
          <ShieldAlert className="h-10 w-10" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">{t.access.forbidden.title}</h2>
        <p className="text-sm max-w-md text-center">
          {t.access.forbidden.description}
        </p>
        <Button variant="outline" onClick={() => navigate('/')}>
          {t.access.forbidden.goHome}
        </Button>
      </div>
    );
  }

  return <>{children}</>;
};

export default FeatureFlagRoute;
